import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import EmptyState from "@/components/feature/EmptyState";
import LoadingState from "@/components/feature/LoadingState";
import { usePageMeta } from "@/hooks/usePageMeta";
import { movies, regions, years, genreOptions } from "@/mocks/movies";
import FilterBar, { type SortKey } from "@/pages/category/components/FilterBar";

const PAGE_SIZE = 8;

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const isValid = type === "movie" || type === "tv";
  const kind = type === "tv" ? "tv" : "movie";
  const typeLabel = kind === "movie" ? "电影" : "电视剧";

  const [genre, setGenre] = useState("all");
  const [region, setRegion] = useState("all");
  const [year, setYear] = useState("all");
  const [sort, setSort] = useState<SortKey>("latest");
  const [loading, setLoading] = useState(true);

  const page = Math.max(1, Number(searchParams.get("page") || "1"));

  usePageMeta({
    title: `${typeLabel}分类 - 生活中的玛丽`,
    description:
      kind === "movie"
        ? "汇聚最新热门电影，支持子类型、地区与年份多项筛选，便于全面了解电影资讯与剧情介绍。"
        : "汇集最新热播电视剧，支持子类型、地区与年份多项筛选，快速了解剧集资讯与剧情。",
    keywords: `生活中的玛丽,${typeLabel}分类,${typeLabel}资讯,${typeLabel}剧情介绍,影视评论`,
  });

  useEffect(() => {
    setGenre("all");
    setRegion("all");
    setYear("all");
    setSort("latest");
    setSearchParams({});
  }, [type, setSearchParams]);

  useEffect(() => {
    setLoading(true);
    const t = window.setTimeout(() => setLoading(false), 320);
    return () => window.clearTimeout(t);
  }, [type, genre, region, year, sort, page]);

  const filtered = useMemo(() => {
    if (!isValid) return [];
    let list = movies.filter((m) => m.type === kind);
    if (genre !== "all") list = list.filter((m) => m.genres.includes(genre));
    if (region !== "all") list = list.filter((m) => m.region === region);
    if (year !== "all") list = list.filter((m) => String(m.year) === year);
    const sorted = [...list];
    if (sort === "latest") sorted.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    if (sort === "hot") sorted.sort((a, b) => b.year - a.year || b.rating - a.rating);
    if (sort === "rating") sorted.sort((a, b) => b.rating - a.rating);
    return sorted;
  }, [isValid, kind, genre, region, year, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const onChange = (patch: { genre?: string; region?: string; year?: string; sort?: SortKey }) => {
    if (patch.genre !== undefined) setGenre(patch.genre);
    if (patch.region !== undefined) setRegion(patch.region);
    if (patch.year !== undefined) setYear(patch.year);
    if (patch.sort !== undefined) setSort(patch.sort);
    setSearchParams({});
  };

  const onReset = () => {
    setGenre("all");
    setRegion("all");
    setYear("all");
    setSort("latest");
    setSearchParams({});
  };

  const goPage = (p: number) => {
    if (p < 1 || p > totalPages) return;
    setSearchParams(p === 1 ? {} : { page: String(p) });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!isValid) {
    return (
      <div className="w-full px-4 md:px-8 py-14">
        <EmptyState
          icon="ri-error-warning-line"
          title="分类不存在"
          description="你访问的分类页面不存在，请从导航重新选择。"
        />
        <div className="flex justify-center">
          <Link
            to="/"
            className="h-10 px-5 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-medium whitespace-nowrap flex items-center gap-2 transition-colors cursor-pointer"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="bg-primary-500">
        <div className="w-full px-4 md:px-8 py-8 md:py-12">
          <div className="text-background-300">
            <Breadcrumb items={[{ label: `${typeLabel}分类` }]} />
          </div>
          <h1 className="mt-4 font-heading font-black text-3xl md:text-4xl text-background-50">
            {typeLabel}分类
          </h1>
          <p className="mt-3 text-sm md:text-base text-background-200 max-w-2xl leading-relaxed">
            {kind === "movie"
              ? "汇聚最新热门电影作品，支持子类型、地区与年份多维筛选，帮你快速找到想看的片子。"
              : "汇集最新热播剧集，支持子类型、地区与年份多维筛选，完整了解每一部剧的剧情与口碑。"}
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-6 md:py-10 space-y-6">
        <FilterBar
          genres={genreOptions[kind]}
          regions={regions}
          years={years}
          selectedGenre={genre}
          selectedRegion={region}
          selectedYear={year}
          sort={sort}
          resultCount={filtered.length}
          onChange={onChange}
          onReset={onReset}
        />

        {loading ? (
          <LoadingState label="正在加载影视作品…" />
        ) : pageItems.length === 0 ? (
          <div className="bg-background-50 rounded-lg border border-background-200">
            <EmptyState
              icon="ri-file-search-line"
              title="未找到符合条件的影视作品"
              description="试试减少筛选条件，或者重置筛选后再看看。"
              actionLabel="重置筛选条件"
              onAction={onReset}
            />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {pageItems.map((movie) => (
                <MovieCard key={movie.id} movie={movie} showType={false} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => goPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="w-9 h-9 rounded-md border border-background-200 bg-background-50 text-foreground-700 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-300 cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line text-lg"></i>
                </button>
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => goPage(i + 1)}
                    className={`min-w-9 h-9 px-3 rounded-md text-sm whitespace-nowrap transition-colors cursor-pointer border ${
                      currentPage === i + 1
                        ? "bg-primary-500 text-background-50 border-primary-500"
                        : "bg-background-50 text-foreground-700 border-background-200 hover:border-primary-300"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => goPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="w-9 h-9 rounded-md border border-background-200 bg-background-50 text-foreground-700 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-300 cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line text-lg"></i>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}